import { useEffect, useRef, useState } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { ResponsiveContainer, Bar, BarChart, XAxis, YAxis, Tooltip } from "recharts";
import { api, apiError, fmtNum, fmtUsd, retryAfterOf } from "@/lib/api";
import { toast } from "sonner";
import TradeModal from "@/components/TradeModal";
import { SignalReport } from "@/components/SignalReport";
import { TradeBracket } from "@/components/TradeBracket";
import { CoinHeader } from "@/components/coin/CoinHeader";
import { MarketDataError, StaleQuoteBanner } from "@/components/coin/MarketDataNotice";
import { CoinChartPanel } from "@/components/coin/CoinChartPanel";
import { CoinIndicators, CoinKeyStats } from "@/components/coin/CoinIndicators";
import { AdvancedSection, BeginnerNote } from "@/components/Beginner";
import { InfoIcon } from "@/components/Hint";
import { CoinNote } from "@/components/coin/CoinNote";
import { useOrderBook } from "@/hooks/useOrderBook";
import { useLiveTicker } from "@/hooks/useLiveTicker";
import { useCoinLiveAlerts } from "@/hooks/useCoinLiveAlerts";
import { useChartSeries } from "@/hooks/useChartSeries";
import { useBilling } from "@/context/BillingContext";
import { UpgradeButton } from "@/components/Locked";
import { useMode } from "@/context/ModeContext";
import { Sparkles } from "lucide-react";

// Shown for coins outside the daily v1 prediction universe — never an invented prediction, just the honest label.
const PriceOnlyCard = ({ name, advanced }) => (
  <div className="apx-card p-4" data-testid="price-only-card">
    <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-400 mb-1">AI predictions</div>
    <div className="text-sm font-medium text-slate-200">Price Data Only</div>
    <div className="text-xs text-slate-500 mt-1">EV-IDEX does not publish daily AI predictions for {name}. Prices, 24h change and charts come from the market-data snapshot.{advanced ? " The on-demand analysis below is a one-off reading and is not part of the audited prediction ledger coverage." : ""}</div>
  </div>
);

export default function CoinDetail() {
  const { coinId } = useParams();
  const { billing, plan } = useBilling();
  const [coin, setCoin] = useState(null);
  const [coinError, setCoinError] = useState(null);
  const [range, setRange] = useState("live");
  const [chart, setChart] = useState({ prices: [], total_volumes: [] });
  const [ind, setInd] = useState(null);
  const [signal, setSignal] = useState(null);
  const [loadingSig, setLoadingSig] = useState(false);
  const [watch, setWatch] = useState(false);
  const [trade, setTrade] = useState({ open: false, side: "buy" });
  const [flags, setFlags] = useState({ showRsi: true, showMacd: true, showBB: false, showMA: true });
  const [chartMode, setChartMode] = useState("candles");
  const [liveInterval, setLiveInterval] = useState(1);
  const [ohlc, setOhlc] = useState([]);
  const [coverage, setCoverage] = useState(null); // { ai_predictions, has_market_data } from the local index
  const { beginner } = useMode();
  const canLive = coverage?.live_eligible === true; // copycat/bridged tokens share a symbol with a major coin: never stream its ticks for them
  const isLive = range === "live" && canLive;
  const histDays = range === "live" ? (canLive ? "7" : "1") : range; // historical range actually requested — "live" is never sent upstream
  const live = useLiveTicker(coin?.symbol, !!coin && canLive, liveInterval);
  const book = useOrderBook(coin?.symbol, isLive && !!coin && !beginner); // order book is an Advanced-mode panel
  useEffect(() => { if (coverage && !coverage.live_eligible && range === "live") setRange("1"); }, [coverage]); // eslint-disable-line react-hooks/exhaustive-deps
  const liveAlerts = useCoinLiveAlerts(coinId, live);
  const [qs] = useSearchParams();
  const fromAlert = qs.get("alert");
  useEffect(() => {
    if (!fromAlert) return;
    api.get("/alerts").then(async (r) => {
      const a = (r.data || []).find((x) => x.id === fromAlert);
      if (a) { toast(`Opened from alert: ${(a.symbol || "").toUpperCase()} ${a.condition || a.kind}`, { description: a.triggered_price ? `Triggered at ${fmtUsd(a.triggered_price)} · ${new Date(a.triggered_at).toLocaleString()}` : undefined, duration: 8000 }); return; }
      const n = ((await api.get("/notifications")).data.notifications || []).find((x) => x.alert_id === fromAlert);
      if (n) toast(`Opened from alert: ${n.title}`, { description: new Date(n.created_at).toLocaleString(), duration: 8000 });
    }).catch(() => {});
  }, [fromAlert]);
  const combined = useChartSeries(chart, range);

  useEffect(() => { if (isLive && live.status === "unavailable") setRange("7"); }, [isLive, live.status]);

  useEffect(() => {
    if (!coverage || isLive || chartMode !== "candles") return;
    api.get(`/coin/${coinId}/ohlc`, { params: { days: histDays } })
      .then((r) => setOhlc((r.data || []).map(([t, o, h, l, c]) => ({ t, o, h, l, c }))))
      .catch(() => setOhlc([]));
  }, [coinId, histDays, isLive, chartMode, coverage]);

  const loadCoin = () => {
    // Keep the last valid quote on screen while refreshing; only a page with no data at all shows the error card.
    const forId = coinId;
    setCoinError(null);
    api.get(`/coin/${forId}`).then((c) => { if (forId !== coinRef.current) return; setCoin(c.data); setCoinError(null); }).catch((e) => {
      if (forId !== coinRef.current) return;
      const ra = e?.retryAfter || retryAfterOf(e);
      const rateLimited = e?.response?.status === 503;
      const until = Date.now() + (ra || (rateLimited ? 30 : 5)) * 1000;
      setCoinError({ rateLimited, until, message: rateLimited ? "Market data is temporarily limited by the provider." : "Could not load coin data." });
    });
  };
  const coinRef = useRef(coinId);
  coinRef.current = coinId;
  const coinErrorRef = useRef(null);
  coinErrorRef.current = coinError;

  // Periodic refresh of the snapshot (served from the shared server cache, 0 provider credits while fresh); skipped inside a back-off window.
  useEffect(() => {
    const t = setInterval(() => { if (!coinErrorRef.current || Date.now() >= coinErrorRef.current.until) loadCoin(); }, 60000);
    return () => clearInterval(t);
  }, [coinId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSignal(null); setInd(null); setCoverage(null); setCoin(null);
    loadCoin();
    api.get(`/coin/${coinId}/coverage`).then((r) => setCoverage(r.data)).catch(() => setCoverage({ ai_predictions: false, has_market_data: false }));
    api.get("/watchlist").then((r) => setWatch(!!r.data.find((c) => c.id === coinId))).catch((e) => console.warn("[apex] ignored:", e?.message || e));
  }, [coinId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cost guard: indicators (and the signal for uncovered coins) are Advanced-mode data — not requested in Beginner mode.
  useEffect(() => {
    if (!coverage) return;
    if (coverage.ai_predictions || !beginner) {
      api.get(`/coin/${coinId}/signal`, { params: { auto: true } }).then((r) => setSignal(r.data || null)).catch((e) => { if (e?.response?.data?.detail?.code === "data_delayed") setSignal({ delayed: true, ...e.response.data.detail }); });
    }
    if (!beginner && ind == null) {
      api.get(`/coin/${coinId}/indicators`, { params: { days: "90" } }).then((i) => setInd(i.data)).catch((e) => console.warn("[apex] request failed:", e?.message || e));
    }
  }, [coinId, coverage, beginner]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!coverage) return; // wait for coverage so the range is settled before spending a market-data call
    api.get(`/coin/${coinId}/chart`, { params: { days: histDays } }).then((ch) => setChart(ch.data)).catch((e) => console.warn("[apex] request failed:", e?.message || e));
  }, [coinId, histDays, coverage]);

  const runAnalysis = async () => {
    if (loadingSig) return; // one in-flight analysis at a time: repeated clicks never double-charge
    setLoadingSig(true);
    try {
      const r = await api.get(`/coin/${coinId}/signal`, { params: { force: true } });
      setSignal(r.data);
      toast.success("Fresh AI analysis ready");
      window.dispatchEvent(new Event("apx:billing-changed"));
    } catch (e) {
      if (e?.response?.data?.detail?.code === "data_delayed") { setSignal({ delayed: true, ...e.response.data.detail }); toast.warning("Data delayed — analysis paused until current market data arrives"); }
      else if (e?.response?.status !== 402) toast.error(apiError(e, "Analysis failed"));
    }
    setLoadingSig(false);
  };

  const toggleWatch = async () => {
    try {
      if (watch) { await api.delete(`/watchlist/${coinId}`); setWatch(false); }
      else { await api.post("/watchlist", { coin_id: coinId, symbol: coin?.symbol, name: coin?.name }); setWatch(true); }
    } catch { toast.error("Watchlist error"); }
  };

  if (coinError && !coin) return <MarketDataError error={coinError} onRetry={loadCoin} />;
  if (!coin) return <CoinSkeleton />;

  const md = coin.market_data || {};
  const price = canLive && ["live", "quiet"].includes(live.status) && live.price != null ? live.price : md.current_price?.usd;
  const change24 = md.price_change_percentage_24h;
  const openTrade = (side) => setTrade({ open: true, side });
  const histAsOf = chartMode === "candles" ? ohlc[ohlc.length - 1]?.t : chart.prices?.[chart.prices.length - 1]?.[0];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-6">
      {coinError && <StaleQuoteBanner error={coinError} asOf={md.last_updated || coin.last_updated} onRetry={loadCoin} priceIsLive={canLive && ["live", "quiet"].includes(live.status) && live.price != null} />}
      <CoinHeader coin={coin} price={price} change24={change24} live={live} watch={watch} onToggleWatch={toggleWatch} onTrade={openTrade} />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-4">
          <CoinChartPanel coin={coin} range={range} setRange={setRange} isLive={isLive} live={live} book={beginner ? undefined : book} canLive={canLive}
            chartMode={chartMode} setChartMode={setChartMode} liveInterval={liveInterval} setLiveInterval={setLiveInterval}
            ohlc={ohlc} combined={combined} up={(change24 ?? 0) >= 0} flags={flags} setFlags={setFlags} alertCount={liveAlerts.alerts.length} histAsOf={histAsOf} />
          <BeginnerNote testid="chart-legend-note">
            <strong className="text-slate-300">Reading this chart <InfoIcon k={isLive ? "live_chart" : chartMode === "candles" ? "candles" : "range_1"} /></strong> — {isLive
              ? "LIVE shows the real exchange price updating every second. Green candles closed higher than they opened, red closed lower. 'Session' is the change since you opened this page; the right-hand panel is the live order book (green = buyers waiting below, red = sellers waiting above)."
              : chartMode === "candles"
                ? "Each candle is one time period: green = price ended higher than it started, red = lower; thin wicks show the extremes. Buttons 24H–1Y change how much history you see. MA = moving-average trend lines, BB = Bollinger 'normal range' bands."
                : "The line is the price over the selected period (24H–1Y). Green shading = the period ended higher than it began; red = lower. Hover any point for the exact price and time."}
          </BeginnerNote>

          {!beginner && (
            <div className="apx-card p-4">
              <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-400 mb-1">Volume</div>
              <div style={{ width: "100%", height: 110 }}>
                <ResponsiveContainer>
                  <BarChart data={combined}>
                    <XAxis dataKey="time" hide />
                    <YAxis stroke="#64748b" fontSize={10} width={60} tickFormatter={(v) => "$" + fmtNum(v)} />
                    <Tooltip contentStyle={{ background: "#0F141C", border: "1px solid #1E293B", borderRadius: 8, fontSize: 12 }} formatter={(v) => "$" + fmtNum(v)} />
                    <Bar dataKey="v" fill="#334155" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {!beginner && <CoinKeyStats md={md} />}
          <CoinNote coin={{ id: coinId, symbol: coin.symbol, name: coin.name }} price={price} />
        </div>

        <div className="lg:col-span-4 space-y-4">
          {coverage && !coverage.ai_predictions && <PriceOnlyCard name={coin.name} advanced={!beginner} />}
          {coverage?.ai_predictions && (
            <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-cyan-300 flex items-center gap-1" data-testid="ai-coverage-badge"><Sparkles className="w-3 h-3" /> AI Predictions · in the daily prediction universe</div>
          )}
          {plan === "free" && billing?.usage?.ai_analyses && (coverage?.ai_predictions || !beginner) && (
            <div className="apx-card p-3 flex items-center gap-3 text-xs" data-testid="signal-quota">
              <div className="flex-1">
                <div className="text-slate-300 font-medium">Free plan · {billing.usage.ai_analyses.used}/{billing.usage.ai_analyses.limit} AI analyses used today</div>
                <div className="text-slate-500">Pro unlocks 40 analyses a day, the Track Record, advanced indicators, brackets and custom alerts.</div>
              </div>
              <UpgradeButton plan="pro" testid="signal-quota-upgrade" label="Upgrade" />
            </div>
          )}
          {(coverage?.ai_predictions || !beginner) && (
            <>
              {coverage && !coverage.ai_predictions && (
                <div className="rounded-lg border border-amber-900/60 bg-amber-500/5 px-3 py-2 text-xs" data-testid="on-demand-banner">
                  <div className="font-semibold text-amber-300 uppercase tracking-widest text-[10px]">On-Demand AI Analysis</div>
                  <div className="text-slate-400 mt-0.5">This analysis is informational and is not part of EV-IDEX's verified prediction track record. It is not added to the audited ledger or public accuracy, and no validated accuracy or predictive edge is claimed for {coin.name}.</div>
                </div>
              )}
              <SignalReport signal={signal} loading={loadingSig} onRefresh={runAnalysis} onPaperTrade={openTrade} />
            </>
          )}
          {!beginner && <TradeBracket symbol={coin.symbol} livePrice={live.price} coinId={coinId} coinName={coin.name} onArmed={liveAlerts.reload} />}
          {!beginner && (
            <div data-testid="advanced-indicators">
              <AdvancedSection title="Technical indicators" k="rsi" testid="advanced-indicators-section"
                what="RSI, MACD, moving averages, Bollinger Bands and programmatic support/resistance — the raw measurements the AI reads."
                why="They show momentum, trend and 'stretched' prices; the Signal Report above already summarises them in plain English.">
                <CoinIndicators ind={ind} sr={signal?.support_resistance} />
              </AdvancedSection>
            </div>
          )}
        </div>
      </div>

      <TradeModal open={trade.open} onOpenChange={(open) => setTrade((t) => ({ ...t, open }))} side={trade.side}
        coin={{ id: coinId, symbol: coin.symbol, name: coin.name, price }} />
    </div>
  );
}

const CoinSkeleton = () => (
  <div className="p-6 space-y-4 max-w-[1600px] mx-auto">
    <div className="skeleton h-14 w-72" />
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <div className="lg:col-span-8 space-y-4">
        <div className="skeleton h-80" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{["a", "b", "c", "d"].map((k) => <div key={k} className="skeleton h-20" />)}</div>
      </div>
      <div className="lg:col-span-4 space-y-4"><div className="skeleton h-60" /><div className="skeleton h-60" /></div>
    </div>
  </div>
);
